import React, { useState, useEffect, useMemo } from "react";
import { Table, Button, Input, Card, Row, Col, Select, Tag, Tooltip } from "antd";
import { EyeOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { Api_Url } from "../../../config/config";
import { ErrorToast } from "../../ToastComponent";
import { EmployeeGrievanceModalAdd } from "./EmployeeGrievanceModalAdd";
import { EmployeeGrievanceModalDetail } from "./EmployeeGrievanceModalDetail";

const { Option } = Select;

const getAuthHeader = () => {
  try {
    const userStr = sessionStorage.getItem("user");
    if (!userStr) {
      console.error("No user in sessionStorage");
      return {};
    }
    const user = JSON.parse(userStr);
    if (!user?.accessToken) {
      console.error("No accessToken in user object", user);
      return {};
    }
    return {
      Authorization: `Bearer ${user.accessToken}`,
    };
  } catch (err) {
    console.error("Error parsing user from sessionStorage:", err);
    return {};
  }
};

export const EmployeeGrievance = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [filters, setFilters] = useState({ searchTerm: "", status: null, type: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      if (!headers.Authorization) {
        ErrorToast("Session expired. Please login again.");
        setLoading(false);
        return;
      }
      const res = await axios.get(
        `${Api_Url}api/v1/EmployeeGrievance?PageNumber=1&PageSize=1000`,
        { headers }
      );
      setData(res?.data?.data || []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to fetch grievances";
      console.error("Fetch grievances error:", err?.response?.status, msg);
      ErrorToast(msg);
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (record) => {
    try {
      const res = await axios.get(
        `${Api_Url}api/v1/EmployeeGrievance/${record.id}`,
        { headers: getAuthHeader() }
      );
      const detail = res?.data?.data;
      if (!detail) {
        ErrorToast("No data returned from server");
        return;
      }
      setSelectedRecord(detail);
      setIsDetailModalOpen(true);
    } catch (err) {
      console.error("openDetail error:", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.statusText ||
        err?.message ||
        "Failed to load grievance details";
      ErrorToast(msg);
    }
  };

  const filteredData = useMemo(() => {
    let list = [...data];
    if (filters.searchTerm) {
      const t = filters.searchTerm.toLowerCase();
      list = list.filter(
        (x) =>
          x.employeeCode?.toLowerCase().includes(t) ||
          x.employeeName?.toLowerCase().includes(t) ||
          x.receiptDetails?.toLowerCase().includes(t)
      );
    }
    if (filters.status) list = list.filter((x) => x.status === filters.status);
    if (filters.type) list = list.filter((x) => x.type === filters.type);
    return list;
  }, [data, filters]);

  const columns = [
    { title: "ID", dataIndex: "id", width: 70, sorter: (a, b) => a.id - b.id },
    {
      title: "Type",
      dataIndex: "type",
      render: (t) => (
        <Tag color={t === "Complaint" ? "red" : "orange"}>{t}</Tag>
      ),
    },
    { title: "Employee ID", dataIndex: "employeeCode" },
    { title: "Employee Name", dataIndex: "employeeName" },
    {
      title: "Receipt Date",
      dataIndex: "receiptDate",
      render: (d) => moment(d).format("DD-MM-YYYY"),
    },
    {
      title: "Receipt Details",
      dataIndex: "receiptDetails",
      ellipsis: true,
    },
    {
      title: "Actions Taken",
      dataIndex: "actionCount",
      width: 110,
      render: (c) => <Tag color="blue">{c}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => (
        <Tag color={s === "Closed" ? "green" : "gold"}>{s?.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Closure Date",
      dataIndex: "closureDate",
      render: (d) => (d ? moment(d).format("DD-MM-YYYY") : "—"),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Tooltip title="View / Add Action">
          <Button
            icon={<EyeOutlined />}
            onClick={() => openDetail(record)}
          >
            View
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Header */}
        <div className="row mb-3">
          <div className="col-12 d-flex align-items-center justify-content-between">
            <h3 className="mb-0">Employee Grievances</h3>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsAddModalOpen(true)}
            >
              New Grievance
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Search by employee code, name, or details..."
                value={filters.searchTerm}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, searchTerm: e.target.value }))
                }
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                placeholder="Type"
                value={filters.type}
                onChange={(v) => setFilters((f) => ({ ...f, type: v }))}
                allowClear
                style={{ width: "100%" }}
              >
                <Option value="Complaint">Complaint</Option>
                <Option value="Grievance">Grievance</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                placeholder="Status"
                value={filters.status}
                onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                allowClear
                style={{ width: "100%" }}
              >
                <Option value="Open">Open</Option>
                <Option value="Closed">Closed</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button
                block
                onClick={() =>
                  setFilters({ searchTerm: "", status: null, type: null })
                }
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Table */}
        <div className="table-responsive">
          <Table
            className="table-striped"
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            loading={loading}
            pagination={{ showSizeChanger: true, defaultPageSize: 10 }}
            scroll={{ x: 1100 }}
          />
        </div>

        {/* Add Modal */}
        {isAddModalOpen && (
          <EmployeeGrievanceModalAdd
            onClose={() => setIsAddModalOpen(false)}
            onSuccess={() => {
              setIsAddModalOpen(false);
              fetchData();
            }}
          />
        )}

        {/* Detail / Action Modal */}
        {isDetailModalOpen && selectedRecord && (
          <EmployeeGrievanceModalDetail
            record={selectedRecord}
            onClose={() => setIsDetailModalOpen(false)}
            onSuccess={() => {
              setIsDetailModalOpen(false);
              fetchData();
            }}
          />
        )}
      </div>
    </div>
  );
};
